<template>
  <div class="patient-select-container" ref="container">
    <label v-if="label" class="patient-select-label">{{ label }}</label>
    <div class="patient-select-input" :class="inputClass" @click="openDropdown" ref="inputWrapper">
      <!-- Selected patient display -->
      <div v-if="selectedPatient" class="selected-patient">
        <img :src="getPatientPicture(selectedPatient)" alt="Patient" class="patient-icon">
        <span class="patient-name">{{ getPatientDisplayName(selectedPatient) }}</span>
        <button type="button" class="clear-button" @click.stop="clearSelection">
          <vue-feather type="x" size="16" />
        </button>
      </div>
      <!-- Input field when no patient selected -->
      <input v-else ref="input" type="text" class="patient-select-field" :placeholder="placeholder"
        :value="query" :disabled="disabled" :inputmode="inputMode" :maxlength="maxLength" autocomplete="nope"
        data-lpignore="true" data-form-type="other" @input="handleInput" @focus="handleFocus" @blur="handleBlur"
        @keydown="handleKeydown" @paste="handlePaste">
    </div>
    <span v-if="error" class="patient-select-error">{{ error }}</span>

    <!-- Dropdown -->
    <FloatingDropdown :open="isOpen && !selectedPatient" :trigger-ref="$refs.inputWrapper" @dismiss="close">
      <div class="patient-dropdown">
        <div class="patient-list" @scroll="handleScroll">
          <!-- Patient results -->
          <div v-for="(patient, index) of patients" :key="patient.id" class="patient-item"
            :class="{ 'patient-item--highlighted': highlightedIndex === index }"
            @mousedown.prevent="selectPatient(patient)" @mouseenter="highlightedIndex = index">
            <img :src="getPatientPicture(patient)" alt="Patient" class="patient-item-icon">
            <div class="patient-item-info">
              <span class="patient-item-name">{{ getPatientDisplayName(patient) }}</span>
            </div>
            <div class="patient-item-contact" v-if="getPatientContact(patient)">
              <img :src="getContactIcon(patient)" alt="Contact" class="contact-icon">
              {{ getPatientContact(patient) }}
            </div>
          </div>

          <!-- Loading indicator -->
          <div class="loading-status" v-if="loading">
            <img src="/loading.svg" class="loading-icon">
          </div>

          <!-- Create new patient option -->
          <div v-if="showCreateOption && doneLoading" class="patient-item create-patient-item"
            :class="{ 'patient-item--highlighted': highlightedIndex === patients.length }"
            @mousedown.prevent="handleCreatePatient" @mouseenter="highlightedIndex = patients.length">
            <div class="create-patient-icon">
              <vue-feather type="plus" size="16" />
            </div>
            <div class="patient-item-info">
              <span class="patient-item-name">Create new patient</span>
            </div>
          </div>
        </div>
      </div>
    </FloatingDropdown>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { FloatingDropdown } from '@/design'
import * as coreApi from '../../api/core.js'
import * as commonUtils from '../../utils/common.js'
import PhoneIcon from '@/assets/icons/phone.png'
import EmailIcon from '@/assets/icons/email.png'

export default {
  name: 'PatientSelectInput',
  components: { VueFeather, FloatingDropdown },
  props: {
    modelValue: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Search by name, phone, or email...'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    showCreateOption: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      query: '',
      isOpen: false,
      isFocused: false,
      loading: false,
      patients: [],
      doneLoading: false,
      searchTimeout: null,
      highlightedIndex: 0
    }
  },
  computed: {
    selectedPatient() {
      return this.modelValue
    },
    inputClass() {
      return {
        'patient-select-input--focused': this.isFocused,
        'patient-select-input--disabled': this.disabled,
        'patient-select-input--error': this.error,
        'patient-select-input--selected': this.selectedPatient
      }
    },
    detectedQueryType() {
      return commonUtils.detectQueryType(this.query)
    },
    detectedFormat() {
      const type = this.detectedQueryType.type
      if (type === 'phone') return 'phone'
      if (type === 'email') return 'email'
      return 'text'
    },
    inputMode() {
      if (this.detectedFormat === 'phone') return 'numeric'
      return 'text'
    },
    maxLength() {
      if (this.detectedFormat === 'phone') return 14
      return undefined
    },
    hasValidContact() {
      if (this.detectedFormat === 'phone') {
        const digitsOnly = this.query.replace(/\D/g, '')
        return digitsOnly.length === 10
      }
      if (this.detectedFormat === 'email') {
        return this.query.includes('@') && this.query.includes('.')
      }
      return false
    },
    hasMatches() {
      return this.patients.length > 0
    }
  },
  watch: {
    query() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.searchPatients()
      }, 300)
      // Emit validity change for parent components
      this.$emit('validityChange', this.hasValidContact, this.hasMatches, this.detectedFormat)
    },
    patients() {
      // Re-emit when matches change
      this.$emit('validityChange', this.hasValidContact, this.hasMatches, this.detectedFormat)
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    this.searchPatients()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    clearTimeout(this.searchTimeout)
  },
  methods: {
    formatPhone(phoneStr) {
      return commonUtils.formatPhone(phoneStr)
    },

    getPatientDisplayName(patient) {
      return commonUtils.getPatientName(patient)
    },

    getPatientPicture(patient) {
      if (!patient.profilePictureKey) {
        return '/icons/patient.png'
      }
      const publicImages = process.env.VUE_APP_PUBLIC_FILES
      return `${publicImages}/${patient.profilePictureKey}`
    },

    getPatientContact(patient) {
      const contactMethods = patient.contactMethods || []

      // If searching by phone, show matching phone
      if (this.detectedFormat === 'phone') {
        const searchPhone = this.query.replace(/\D/g, '')
        const phones = contactMethods.filter(cm => cm.type === 'PHONE')
        for (const phone of phones) {
          const phoneDigits = (phone.phoneNumber || '').replace(/\D/g, '')
          if (phoneDigits.includes(searchPhone) || searchPhone.includes(phoneDigits.slice(-10))) {
            return this.formatPhone(phone.phoneNumber)
          }
        }
        if (phones.length > 0) {
          return this.formatPhone(phones[0].phoneNumber)
        }
      }

      // If searching by email, show matching email
      if (this.detectedFormat === 'email') {
        const emails = contactMethods.filter(cm => cm.type === 'EMAIL')
        if (emails.length > 0) {
          return emails[0].emailAddress
        }
      }

      // Default: show first phone if available
      const phones = contactMethods.filter(cm => cm.type === 'PHONE')
      if (phones.length > 0) {
        return this.formatPhone(phones[0].phoneNumber)
      }

      return ''
    },

    getContactIcon() {
      if (this.detectedFormat === 'email') {
        return EmailIcon
      }
      return PhoneIcon
    },

    openDropdown() {
      if (this.disabled || this.selectedPatient) return
      this.isOpen = true
      this.$nextTick(() => {
        this.$refs.input?.focus()
      })
    },

    handleFocus() {
      this.isFocused = true
      this.isOpen = true
      if (this.patients.length === 0 && !this.loading) {
        this.searchPatients()
      }
    },

    handleBlur() {
      this.isFocused = false
      setTimeout(() => {
        if (!this.isFocused) {
          this.isOpen = false
        }
      }, 150)
    },

    handleClickOutside(event) {
      const clickedDropdown = this.$refs.dropdown && this.$refs.dropdown.contains(event.target)
      const clickedContainer = this.$refs.container && this.$refs.container.contains(event.target)
      if (!clickedDropdown && !clickedContainer) {
        this.isOpen = false
      }
    },

    handleInput(event) {
      const input = event.target
      const oldValue = this.query || ''
      let newValue = input.value
      let cursorPos = input.selectionStart

      if (this.detectedFormat === 'phone') {
        const result = this.formatPhoneInput(newValue, oldValue, cursorPos)
        newValue = result.value
        cursorPos = result.cursor
      }

      this.query = newValue
      this.highlightedIndex = 0

      if (this.detectedFormat === 'phone') {
        this.$nextTick(() => {
          input.setSelectionRange(cursorPos, cursorPos)
        })
      }
    },

    handlePaste(event) {
      if (this.detectedFormat === 'phone') {
        event.preventDefault()
        const paste = (event.clipboardData || window.clipboardData).getData('text')
        const digitsOnly = paste.replace(/\D/g, '')

        const input = this.$refs.input
        const start = input.selectionStart
        const end = input.selectionEnd
        const currentValue = this.query || ''

        const newValue = currentValue.slice(0, start) + digitsOnly + currentValue.slice(end)
        const result = this.formatPhoneInput(newValue, currentValue, start + digitsOnly.length)
        this.query = result.value
        this.$nextTick(() => {
          input.setSelectionRange(result.cursor, result.cursor)
        })
      }
    },

    handleKeydown(event) {
      if (this.detectedFormat === 'phone') {
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End']
        const isDigit = /^\d$/.test(event.key)
        const isAllowedKey = allowedKeys.includes(event.key)
        const hasModifier = event.ctrlKey || event.metaKey || event.altKey

        if (!isDigit && !isAllowedKey && !hasModifier) {
          event.preventDefault()
          return
        }
      }

      const totalItems = this.patients.length + (this.showCreateOption && this.doneLoading ? 1 : 0)

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, totalItems - 1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (this.highlightedIndex < this.patients.length) {
          this.selectPatient(this.patients[this.highlightedIndex])
        } else if (this.showCreateOption && this.highlightedIndex === this.patients.length) {
          this.handleCreatePatient()
        }
      } else if (event.key === 'Escape') {
        this.isOpen = false
        this.$refs.input?.blur()
      }

      if (this.detectedFormat === 'phone' && event.key === 'Backspace') {
        const input = this.$refs.input
        const pos = input.selectionStart
        const value = this.query || ''

        if (pos > 0 && /\D/.test(value[pos - 1])) {
          event.preventDefault()
          let newPos = pos - 1
          while (newPos > 0 && /\D/.test(value[newPos - 1])) {
            newPos--
          }
          if (newPos > 0) {
            const newValue = value.slice(0, newPos - 1) + value.slice(pos)
            this.query = this.formatPhoneInput(newValue, value, newPos - 1).value
            this.$nextTick(() => {
              input.setSelectionRange(newPos - 1, newPos - 1)
            })
          }
        }
      }
    },

    formatPhoneInput(value, _oldValue, cursorPos) {
      let digits = value.replace(/\D/g, '')
      if (digits.length > 10) {
        digits = digits.slice(0, 10)
      }

      const rawBeforeCursor = value.slice(0, cursorPos)
      const digitsBeforeCursor = Math.min(rawBeforeCursor.replace(/\D/g, '').length, 10)

      let formatted = ''
      if (digits.length > 0) {
        formatted = '('
        formatted += digits.slice(0, 3)
        if (digits.length >= 3) {
          formatted += ') '
          formatted += digits.slice(3, 6)
          if (digits.length >= 6) {
            formatted += '-'
            formatted += digits.slice(6, 10)
          }
        }
      }

      let newCursor = 0
      let digitCount = 0
      for (let i = 0; i < formatted.length && digitCount < digitsBeforeCursor; i++) {
        newCursor = i + 1
        if (/\d/.test(formatted[i])) {
          digitCount++
        }
      }

      if (newCursor < formatted.length) {
        while (newCursor < formatted.length && !/\d/.test(formatted[newCursor])) {
          newCursor++
        }
      }

      return { value: formatted, cursor: newCursor }
    },

    buildSearchParams() {
      const params = {}
      if (!this.query || this.query.trim() === '') {
        return params
      }

      const detected = this.detectedQueryType
      switch (detected.type) {
        case 'email':
          params.email = detected.value
          break
        case 'phone':
          params.phone = detected.value
          break
        case 'name':
        default:
          params.name = detected.value
          break
      }

      return params
    },

    async searchPatients() {
      try {
        // Don't clear list or show loading - keep showing previous results until new ones arrive
        this.doneLoading = false

        const searchParams = this.buildSearchParams()
        const { patients, isLast } = await coreApi.searchPatients(searchParams)

        // Instant swap to new results
        this.patients = patients
        this.doneLoading = isLast
        this.highlightedIndex = 0
      } catch (err) {
        console.error('Failed to search patients:', err)
      }
    },

    async loadMorePatients() {
      if (this.loading || this.doneLoading) return

      try {
        this.loading = true
        const lastPatient = this.patients[this.patients.length - 1]
        const searchParams = this.buildSearchParams()
        searchParams.afterPatientId = lastPatient.id
        const { patients, isLast } = await coreApi.searchPatients(searchParams)

        for (const patient of patients) {
          this.patients.push(patient)
        }
        this.doneLoading = isLast
      } catch (err) {
        console.error('Failed to load more patients:', err)
      } finally {
        this.loading = false
      }
    },

    handleScroll(event) {
      if (this.doneLoading) return

      const list = event.target
      const maxScroll = list.scrollHeight - list.offsetHeight
      const percentScrolled = list.scrollTop / maxScroll

      if (percentScrolled >= 0.9) {
        this.loadMorePatients()
      }
    },

    selectPatient(patient) {
      this.$emit('update:modelValue', patient)
      this.$emit('select', patient)
      this.isOpen = false
      this.query = ''
    },

    clearSelection() {
      this.$emit('update:modelValue', null)
      this.$emit('clear')
      this.query = ''
      this.$nextTick(() => {
        this.$refs.input?.focus()
      })
    },

    focus() {
      if (this.selectedPatient) {
        this.clearSelection()
      } else {
        this.$refs.input?.focus()
      }
    },

    getRawQuery() {
      if (this.detectedFormat === 'phone') {
        return this.query.replace(/\D/g, '')
      }
      return this.query
    },

    handleCreatePatient() {
      this.isOpen = false
      const rawQuery = this.getRawQuery()
      this.$emit('createPatient', {
        phoneNumber: this.detectedFormat === 'phone' ? rawQuery : '',
        emailAddress: this.detectedFormat === 'email' ? this.query : '',
        name: this.detectedFormat === 'text' ? this.query : ''
      })
    },

    close() {
      this.isOpen = false
    }
  },
  emits: ['update:modelValue', 'select', 'clear', 'validityChange', 'createPatient']
}
</script>

<style scoped>
.patient-select-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.patient-select-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.patient-select-input {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 45px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: all 0.15s ease;
  cursor: text;
}

.patient-select-input:hover:not(.patient-select-input--disabled):not(.patient-select-input--error) {
  border-color: #9ca3af;
}

.patient-select-input--focused:not(.patient-select-input--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.patient-select-input--disabled {
  background: #f3f4f6;
  opacity: 0.6;
  cursor: not-allowed;
}

.patient-select-input--error {
  border-color: #dc2626;
}

.patient-select-input--error.patient-select-input--focused {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.patient-select-input--selected {
  cursor: default;
}

.patient-select-field {
  width: 100%;
  height: 100%;
  padding: 0 14px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
  box-sizing: border-box;
}

.patient-select-field::placeholder {
  color: #9ca3af;
}

.patient-select-field:disabled {
  cursor: not-allowed;
}

.patient-select-error {
  font-size: 0.8125rem;
  color: #dc2626;
}

/* Selected patient display */
.selected-patient {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  width: 100%;
  height: 100%;
}

.selected-patient .patient-icon {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  flex-shrink: 0;
}

.selected-patient .patient-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.clear-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.clear-button:hover {
  background: #f3f4f6;
  color: #374151;
}
</style>

<style>
/* Dropdown styles - not scoped so they apply when teleported */
.patient-dropdown {
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.patient-dropdown .patient-list {
  max-height: min(var(--fd-max-height, 9999px), 280px);
  overflow-y: auto;
}

.patient-dropdown .patient-item {
  padding: 10px 12px;
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
  transition: background 0.1s ease;
}

.patient-dropdown .patient-item:hover,
.patient-dropdown .patient-item--highlighted {
  background: var(--color-fill-med, #f3f4f6);
}

.patient-dropdown .patient-item-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  flex-shrink: 0;
}

.patient-dropdown .patient-item-info {
  flex: 1;
  overflow: hidden;
}

.patient-dropdown .patient-item-name {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.patient-dropdown .patient-item-contact {
  font-size: 0.8125rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.patient-dropdown .contact-icon {
  width: 14px;
  height: 14px;
}

.patient-dropdown .loading-status {
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.patient-dropdown .loading-icon {
  height: 24px;
}

.patient-dropdown .create-patient-item {
  border-top: 1px solid #e5e7eb;
}

.patient-dropdown .create-patient-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--color-fill-med, #f3f4f6);
  color: #666;
  transition: transform 0.15s ease;
}

.patient-dropdown .create-patient-item:hover .create-patient-icon {
  transform: scale(1.15);
}
</style>
